import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuChevronRight as ChevronRight, LuTruck as TruckIcon } from 'react-icons/lu'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { useChartTheme } from '../../../hooks/useChartTheme'
import { brand, themeColors, statusColors } from '../../../theme'
import { transportistaStats } from '../../../data/mockEstadisticas'
import CustomTooltip from '../components/CustomTooltip'
import ChartCard from '../components/ChartCard'

const MotionBox = motion.create(Box)

export default function DetalleTransportistasPage() {
  const { axis, grid } = useChartTheme()
  const colors = themeColors
  const navigate = useNavigate()

  const flotaData = [...transportistaStats]
    .sort((a, b) => b.utilizacionFlota - a.utilizacionFlota)
    .map((t) => ({
      nombre: t.nombre.length > 18 ? t.nombre.slice(0, 18) + '...' : t.nombre,
      utilizacion: t.utilizacionFlota,
      flota: t.flota,
    }))

  const rankingData = [...transportistaStats]
    .sort((a, b) => b.viajesCompletados - a.viajesCompletados)

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex gap={2} alignItems="center" mb={2}>
          <Text fontSize="sm" color={brand.green} cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/estadisticas')}>
            Estadisticas
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            Transportistas
          </Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color={colors.text}>
          Detalle por Transportista
        </Text>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mt={1}>
          {transportistaStats.length} transportistas registrados
        </Text>
      </MotionBox>

      {/* Summary KPIs */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
        {[
          { label: 'Transportistas', value: transportistaStats.length.toString(), unit: '' },
          { label: 'Total Flota', value: transportistaStats.reduce((a, t) => a + t.flota, 0).toString(), unit: 'vehiculos' },
          { label: 'Conductores', value: transportistaStats.reduce((a, t) => a + t.conductores, 0).toString(), unit: '' },
          { label: 'Tonelaje Transportado', value: `${(transportistaStats.reduce((a, t) => a + t.tonelajeTransportado, 0) / 1000).toFixed(1)}k`, unit: 'TM' },
        ].map((kpi, i) => (
          <MotionBox
            key={kpi.label}
            bg={colors.bgCard}
            border="1px solid"
            borderColor={colors.border}
            borderRadius="2xl"
            p={5}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            boxShadow="none"
          >
            <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mb={2}>
              {kpi.label}
            </Text>
            <Flex alignItems="baseline" gap={2}>
              <Text fontSize="xl" fontWeight="bold" color={colors.text}>
                {kpi.value}
              </Text>
              {kpi.unit && (
                <Text fontSize="sm" color={colors.textMuted}>
                  {kpi.unit}
                </Text>
              )}
            </Flex>
          </MotionBox>
        ))}
      </Grid>

      {/* Fleet Utilization Chart */}
      <ChartCard title="Utilizacion de Flota" subtitle="Porcentaje de vehiculos que realizaron viajes en el periodo" delay={0.2}>
        <Box h="360px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={flotaData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid stroke={grid.stroke} strokeDasharray={grid.strokeDasharray} horizontal={false} />
              <XAxis type="number" stroke={axis.stroke} tick={axis.tick} domain={[0, 100]} unit="%" />
              <YAxis type="category" dataKey="nombre" stroke={axis.stroke} tick={axis.tick} width={150} />
              <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
              <Bar dataKey="utilizacion" name="Utilizacion %" radius={[0, 4, 4, 0]} barSize={20}>
                {flotaData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.utilizacion >= 85 ? statusColors.success : entry.utilizacion >= 75 ? brand.green : statusColors.warning}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Detailed Table */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        boxShadow="none"
      >
        <Box p={6} borderBottom="1px solid" borderColor={colors.border}>
          <Text fontSize="lg" fontWeight="bold" color={colors.text}>
            Ranking de Transportistas
          </Text>
        </Box>
        <Box overflowX="auto">
          <Box as="table" width="100%" textAlign="left">
            <Box as="thead">
              <Box as="tr" bg={colors.bgHover}>
                {['#', 'Transportista', 'RUC', 'Flota', 'Conductores', 'Viajes', 'Tonelaje', 'Utilizacion'].map((h) => (
                  <Box as="th" key={h} px={5} py={4} fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted}>
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {rankingData.map((t, i) => (
                <Box as="tr" key={t.id} borderBottom="1px solid" borderColor={colors.border} _hover={{ bg: colors.bgHover }}>
                  <Box as="td" px={5} py={4}>
                    <Flex alignItems="center" justifyContent="center" w="24px" h="24px" borderRadius="full" bg={i < 3 ? `${brand.green}22` : 'transparent'} color={i < 3 ? brand.green : colors.textMuted}>
                      <Text fontSize="xs" fontWeight="bold">{i + 1}</Text>
                    </Flex>
                  </Box>
                  <Box as="td" px={5} py={4}>
                    <Flex alignItems="center" gap={2}>
                      <TruckIcon size={14} color={colors.textMuted} />
                      <Text fontSize="sm" fontWeight="600" color={colors.text}>{t.nombre}</Text>
                    </Flex>
                  </Box>
                  <Box as="td" px={5} py={4}><Text fontSize="sm" color={colors.textMuted}>{t.ruc}</Text></Box>
                  <Box as="td" px={5} py={4}><Text fontSize="sm" color={colors.text}>{t.flota}</Text></Box>
                  <Box as="td" px={5} py={4}><Text fontSize="sm" color={colors.text}>{t.conductores}</Text></Box>
                  <Box as="td" px={5} py={4}><Text fontSize="sm" color={colors.text}>{t.viajesCompletados.toLocaleString()}</Text></Box>
                  <Box as="td" px={5} py={4}><Text fontSize="sm" color={colors.text}>{t.tonelajeTransportado.toLocaleString()} TM</Text></Box>
                  <Box as="td" px={5} py={4}>
                    <Flex alignItems="center" gap={2}>
                      <Box flex={1} bg="#efe8dd" borderRadius="full" h="6px" overflow="hidden">
                        <Box bg={t.utilizacionFlota >= 85 ? statusColors.success : brand.green} h="100%" borderRadius="full" width={`${t.utilizacionFlota}%`} />
                      </Box>
                      <Text fontSize="xs" color={colors.textMuted} minW="35px">{t.utilizacionFlota}%</Text>
                    </Flex>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </MotionBox>
    </VStack>
  )
}
